import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { API } from '../constants';
import { AppService } from './app.service';

export interface AffiliateData {
  orgResults: any[];
  fanPrograms: any[];
}

export interface AffiliateFlags {
  isAffiliate: boolean;
  isGroupAffiliate: boolean;
  affiliateKey: number | string;
}

export interface AffiliateLinkContext {
  organizationKey: string;
  organizationId: any;
  organizationName: string;
  affiliateKey: number | string;
  userInfo: any;
  activeGroup?: any;
}

/**
 * Shared "Become an Affiliate" / "Sign in as Affiliate" rules - same behaviour as
 * the ic-header button, scoped to the network or the active group.
 */
@Injectable({ providedIn: 'root' })
export class AffiliateService {
  private inFlight = new Map<string, Observable<any>>();

  constructor(private appService: AppService) {}

  /**
   * The ic-header and the AI agent both request these on the same performer-info update;
   * identical requests still in flight share one call. The entry is dropped once the request
   * finishes, so any later call goes to the network as before (no caching).
   */
  private shareInFlight(key: string, request: () => Observable<any>): Observable<any> {
    let shared = this.inFlight.get(key);
    if (!shared) {
      shared = request().pipe(
        finalize(() => this.inFlight.delete(key)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.inFlight.set(key, shared);
    }
    return shared;
  }

  /** Raw GET_ORGANIZATIONS_BY_AFFILIATE response for this email. */
  getOrganizationsByAffiliate(email: string): Observable<any> {
    return this.shareInFlight(`orgs|${email}`,
      () => this.appService.postRequest(API.GET_ORGANIZATIONS_BY_AFFILIATE, { email }));
  }

  /** Raw GET_AFFILIATE_PROGRAMS response for this organization. */
  getAffiliatePrograms(organizationId: any): Observable<any> {
    return this.shareInFlight(`programs|${organizationId}`,
      () => this.appService.getRequest(`${API.GET_AFFILIATE_PROGRAMS}/${organizationId}`));
  }

  /** Fetches the user's affiliate records and the org's fan affiliate programs. */
  loadData(email: string, organizationId: any): Observable<AffiliateData> {
    const orgResults$: Observable<any[]> = this.getOrganizationsByAffiliate(email)
      .pipe(
        map((res: any) => (Array.isArray(res?.result) ? res.result : [])),
        catchError(() => of([]))
      );
    const fanPrograms$: Observable<any[]> = this.getAffiliatePrograms(organizationId)
      .pipe(
        map((res: any) => (res?.status == 0 && Array.isArray(res.result) ? res.result : [])
          .filter((item: any) => item.role_type === 'fan')),
        catchError(() => of([]))
      );
    return forkJoin({ orgResults: orgResults$, fanPrograms: fanPrograms$ });
  }

  /**
   * Group context: affiliate only if a record matches the group name.
   * Network context: affiliate only if an org-level record (no group) exists.
   */
  evaluate(orgResults: any[], organizationKey: string, activeGroup?: any): AffiliateFlags {
    const orgRecords = (orgResults || []).filter((org: any) => org.organization_key === organizationKey);
    const groupName = (activeGroup?.name || activeGroup?.group_name || '').toLowerCase();
    const matches = (org: any) => activeGroup
      ? org.network_group_name?.toLowerCase() === groupName
      : !org.network_group_name;

    const matched = orgRecords.find(matches);
    const isAffiliate = !!matched;
    return {
      isAffiliate,
      isGroupAffiliate: !isAffiliate && orgRecords.length > 0,
      affiliateKey: matched?.affiliate_key || 0,
    };
  }

  isVisible(flags: AffiliateFlags, fanPrograms: any[]): boolean {
    return flags.isAffiliate || flags.isGroupAffiliate || (fanPrograms?.length || 0) > 0;
  }

  buildUrl(ctx: AffiliateLinkContext): string {
    const user = ctx.userInfo || {};
    const group = ctx.activeGroup;
    const groupName = group?.name || group?.group_name;
    const groupId = group?.id || group?.group_id;
    const payload = {
      role_type: 'fan',
      email: user.email || '',
      fan_user_id: user.id || '',
      actual_user_id: user.actual_user_id || '',
      first_name: user.user_first_name || '',
      last_name: user.user_last_name || '',
      phone_number: user.user_phone_number || '',
      ...(group?.group_key && { group_key: group.group_key }),
      ...(groupId && { network_group_id: groupId }),
      ...(groupName && { network_group_name: groupName }),
      organization_id: ctx.organizationId || null,
      organization_name: ctx.organizationName || '',
    };
    const encoded = btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(payload))));
    const hasKey = !!ctx.affiliateKey && ctx.affiliateKey !== '0';
    const path = hasKey
      ? `affiliate-signin/${ctx.organizationKey}/${ctx.affiliateKey}`
      : `affiliate-register/${ctx.organizationKey}/0`;
    return `${environment.VENDOR_PORTAL}${path}?data=${encodeURIComponent(encoded)}`;
  }
}
